import isEqual from 'lodash/isEqual'
import memoize from 'lodash/memoize'

import type { ISO8601 } from '@app/constants/Misc'
import { DEFAULT_SCHOOL_PHONE } from '@app/constants/Misc'

import { assertNever } from '@app/utils/assertNever'
import { createSelector } from '@app/utils/createSelector'
import { createSortHandler } from '@app/utils/createSortHandler'
import moment from '@app/utils/moment'
import { normalize } from '@app/utils/normalizer'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiContact, ApiRegion } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { compareLoadables, createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { telegramContactEnabledResultSelector, whatsappContactEnabledResultSelector } from './app_config'
import { profileRegionIdResultSelector } from './profile_place'
import { regionsMapSelector } from './regions'
import { routingDateSelector } from './routing'

const contactsSlice = createReduxSlice<{ date: ISO8601; contacts: { [key: string]: ApiContact } }>('contacts')

const contactsModelsResultSelector = createSelector(
  [contactsSlice.selector, routingDateSelector],
  (meta, date) =>
    createLoadable(c => {
      if (!meta || moment(meta.date).add(30, 'minutes').isBefore(date)) {
        return c.load(
          createPromise('Contacts must be fetched', date.format(), async dispatch => {
            const { getContacts } = await import('@app/packages/api/v2/actions/contacts')
            const resp = await dispatch(callV2Api(getContacts())).catch(catchFetchDataError(dispatch, 'Contacts fetch failed'))
            const { contacts } = normalize(resp)
            dispatch(contactsSlice.set({ date: date.format(), contacts: contacts ?? {} }))
          })
        )
      }
      return meta.contacts
    }),
  {
    resultEqual: (a, b) => compareLoadables(a, b, isEqual),
  }
)

export const contactsListResultSelector = createSelector(
  [contactsModelsResultSelector, regionsMapSelector, whatsappContactEnabledResultSelector, telegramContactEnabledResultSelector],
  (modelsResult, regions, whatsappEnabledResult, telegramEnabledResult) =>
    createLoadable(c => {
      const models = c.unwrap(modelsResult)
      const whatsappEnabled = c.unwrap(whatsappEnabledResult)
      const telegramEnabled = c.unwrap(telegramEnabledResult)
      const contacts = Object.values(models)
        .map(c => {
          const regionId = c.relationships.region.data?.id
          const region = regionId ? regions[regionId] : null
          if (regionId && !region) throw new Error("Can't find region")

          return {
            id: c.id,
            attributes: {
              ...c.attributes,
              whatsapp: (whatsappEnabled ? c.attributes.whatsapp : null) ?? null,
              telegram: (telegramEnabled ? c.attributes.telegram : null) ?? null,
            },
            region,
          }
        })
        .toSorted(createSortHandler(a => [a.region?.id ? getRegionWeight(a.region) : -1, a.region?.id ? -parseFloat(a.region.id) : 1, -parseFloat(a.id)]))

      return contacts
    })
)

export const contactsSchoolsResultSelector = mapThunkSelector(contactsListResultSelector, contacts => {
  return contacts.reduce<Partial<Record<string, ApiContact['attributes']>>>((acc, c) => {
    if (c.attributes.department === 'school' && !!c.region) {
      acc[c.region.id] = c.attributes
    }
    return acc
  }, {})
})

export const contactsAcademySelector = mapThunkSelector(contactsListResultSelector, contacts => contacts.find(c => c.attributes.department === 'academy'))

export const createContactDataSelector = memoize((area: 'main' | 'sitter' | 'school') =>
  createSelector([profileRegionIdResultSelector, contactsListResultSelector], (regionIdResult, contactsResult) =>
    createLoadable(c => {
      const regionId = c.unwrap(regionIdResult)
      const contacts = c.unwrap(contactsResult)
      const phone = (() => {
        const defaultPhone = contacts.find(c => c.attributes.department === 'support')?.attributes.phone ?? DEFAULT_SCHOOL_PHONE
        switch (area) {
          case 'main':
            return defaultPhone
          case 'sitter':
            return defaultPhone
          case 'school':
            return (
              (
                contacts.find(c => c.attributes.department === 'school' && c.region && c.region.id === regionId && c.attributes.phone) ??
                contacts.find(c => c.attributes.department === 'school' && !c.region && c.attributes.phone)
              )?.attributes.phone ?? defaultPhone
            )
          default:
            return assertNever(area)
        }
      })()

      return { phone }
    })
  )
)

function getRegionWeight(region: ApiRegion) {
  return REGION_WEIGHTS.indexOf(region.attributes.type) + 1
}

const REGION_WEIGHTS = ['world', 'country', 'city']
